import { m } from "#/paraglide/messages";
import {
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@beweegbaas/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { InsightsSubnavigation } from "#/components/insights-subnavigation";
import { Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { statisticsSearchSchema } from "#/modules/klassen.statistics/schema.search";
import { getStatistics } from "#/modules/klassen.statistics/statistics.functions";
import { AveragesTable } from "#/modules/school.klassen/components/averages-table";
import { ScoreLegend } from "#/modules/util.score-status/components/score-status";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/insights/averages/",
)({
  validateSearch: statisticsSearchSchema,
  // Narrowed to exactly the keys the loader/server-fn uses — `tab` is display-only (AveragesTable),
  // never forwarded to the server.
  loaderDeps: ({ search }) => ({ klasId: search.klasId, leerjaar: search.leerjaar }),
  loader: ({ deps }) => getStatistics({ data: deps }),
  component: Statistics,
  errorComponent: StatisticsError,
});

function Statistics() {
  const {
    klassen,
    leerlijnAveragesData,
    sociaalEmotioneelThemaDomeinAveragesData,
    selectedKlasId,
  } = Route.useLoaderData();
  // Explicit URL selection first, else the server-resolved default (first class);
  // the URL stays param-free on first visit.
  const { klasId: searchKlasId } = Route.useSearch();
  const klasId = searchKlasId ?? selectedKlasId;
  const navigate = Route.useNavigate();

  const hasNoClasses = klassen.length === 0;
  const klasItems = klassen.map((klas) => ({
    id: klas.id,
    name: klas.name,
  }));

  return (
    <Page>
      {!hasNoClasses && (
        <Page.Header>
          <Select
            value={klasId?.toString() || ""}
            onValueChange={(value: string | null) => {
              void navigate({
                search: (prev) => ({ ...prev, klasId: value || undefined }),
                replace: true,
                resetScroll: false,
              });
            }}
          >
            <SelectTrigger aria-label="Klas" className={"w-full md:w-xs"}>
              <SelectValue>
                {klasId
                  ? klasItems.find((item) => item.id.toString() === klasId?.toString())?.name ||
                    m["common.placeholders.select_klas"]()
                  : m["common.placeholders.select_klas"]}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {klasItems.map((item) => (
                  <SelectItem key={item.id} value={item.id.toString()}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Page.Header>
      )}
      {hasNoClasses && <Page.MobileHeader />}
      <InsightsSubnavigation />
      {hasNoClasses ? (
        <div className="flex grow">
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m["common.empty_state.no_classes"]()}</EmptyTitle>
              <EmptyDescription>
                Importeer eerst leerlingen om statistieken per klas te bekijken.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                size="xl"
                render={
                  <Link
                    from={Route.fullPath}
                    to="/$locale/{-$schoolyear}/$school/detail/import/$type"
                    params={(prev: Record<string, string>) => ({
                      ...prev,
                      type: "modify-existing-year",
                    })}
                  />
                }
                nativeButton={false}
              >
                {m["common.empty_state.import_students"]()}
                <IconMaterialSymbolsArrowRightAltRounded />
              </Button>
            </EmptyContent>
          </Empty>
        </div>
      ) : (
        <Page.Body className="min-h-0">
          <PendingContent className="flex flex-col gap-6">
            {klasId && leerlijnAveragesData && sociaalEmotioneelThemaDomeinAveragesData && (
              <>
                <AveragesTable
                  mode="klas"
                  alignToContainer
                  leerlijnData={leerlijnAveragesData}
                  sociaalEmotioneelData={sociaalEmotioneelThemaDomeinAveragesData}
                />
                <Page.Container className="flex justify-center">
                  <ScoreLegend />
                </Page.Container>
              </>
            )}
          </PendingContent>
        </Page.Body>
      )}
    </Page>
  );
}

function StatisticsError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
